/**
 * Sale schema for the duplicate-group comparison engine.
 *
 * Describes how a duplicate-SALE group member renders: identity header
 * (LS / Xero links via the canonical `InvoiceLinkCell` system icons) and
 * the attribute rows the detector's verdict hinges on (dates, client,
 * totals, line signature). Structure and behaviour live in
 * `GroupComparisonTable`; this file is content only.
 */
import React from 'react'
import { Tooltip, Typography } from '@mui/material'

import InvoiceLinkCell from '../../../../shared/InvoiceLinkCell'
import {
  formatBusinessDate,
  formatCurrency,
} from '../../../../../utils/inspectorFormatters'
import { NUMERIC_FONT_SX } from '../../../../../theme/numericFont'
import type { DuplicateSaleGroupMember } from '../../../../../services/inspector/api'
import type { GroupComparisonSchema } from '../comparisonTypes'

const fmtClient = (m: DuplicateSaleGroupMember): string => {
  if (m.customer_company && m.customer_company.trim()) {
    return m.customer_company.trim()
  }
  const full = `${m.customer_first_name || ''} ${m.customer_last_name || ''}`.trim()
  return full || 'Walk-in'
}

const fmtSaleTimestamp = (raw?: string | null): string => {
  if (!raw) return '-'
  try {
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return raw
    return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z')
  } catch {
    return raw
  }
}

// Invoice identifiers take the canonical tabular stack (same treatment as
// the Compare Sales surface) - digits align without the typewriter look.
// True monospace is reserved for the hash renders below.
const tabularId = (text: string): React.ReactNode => (
  <Typography variant="body2" sx={{ ...NUMERIC_FONT_SX, fontWeight: 500 }}>
    {text}
  </Typography>
)

const truncatedHash = (value?: string | null): React.ReactNode =>
  value ? (
    <Tooltip title={value}>
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
        {value.slice(0, 8)}…
      </Typography>
    </Tooltip>
  ) : (
    '-'
  )

export const saleComparisonSchema: GroupComparisonSchema<DuplicateSaleGroupMember> =
  {
    domain: 'sale',
    exceptionCode: 'ex-sale-ls-duplicate',
    exceptionTypeName: 'Duplicate sale',
    cornerLabel: 'Sale',
    minColumnWidth: 200,
    memberId: (m) => String(m.ls_id),
    memberTitle: (m) =>
      m.invoice_number
        ? `Invoice ${m.invoice_number}`
        : `Sale ${String(m.ls_id).slice(0, 8)}`,
    memberHeader: (m) => (
      <InvoiceLinkCell
        lightspeed={
          m.lightspeed_link
            ? {
                id: m.ls_id,
                invoiceNumber: m.invoice_number ?? undefined,
                saleLink: m.lightspeed_link,
              }
            : undefined
        }
        xero={
          m.xero_link
            ? {
                id: m.xero_invoice_id ?? undefined,
                reference: m.invoice_number ?? undefined,
                invoiceLink: m.xero_link,
              }
            : undefined
        }
        iconSize={13}
        layout="vertical"
      />
    ),
    attrs: [
      {
        key: 'invoice_number',
        label: 'Invoice #',
        getValue: (m) => m.invoice_number || '',
        render: (m) => tabularId(m.invoice_number || '-'),
      },
      {
        key: 'sale_date',
        label: 'Sale date',
        getValue: (m) => m.sale_date_local || '',
        render: (m) =>
          m.sale_date_local ? formatBusinessDate(m.sale_date_local) : '-',
        numeric: true,
      },
      {
        key: 'sale_timestamp',
        label: 'Sale timestamp (POS)',
        getValue: (m) => m.sale_date || '',
        render: (m) => fmtSaleTimestamp(m.sale_date),
        numeric: true,
      },
      {
        key: 'client',
        label: 'Client',
        getValue: (m) => fmtClient(m),
      },
      {
        key: 'outlet',
        label: 'Outlet',
        getValue: (m) => m.outlet_name || m.outlet_id || '',
        render: (m) => m.outlet_name || m.outlet_id || '-',
      },
      {
        key: 'register',
        label: 'Register',
        getValue: (m) => m.register_id || '',
        render: (m) => (m.register_id ? m.register_id.slice(-6) : '-'),
      },
      {
        key: 'status',
        label: 'Status',
        // m.status is the LS sale status (the lifecycle field is tag_status).
        getValue: (m) => m.status || '',
        render: (m) => m.status || '-',
      },
      {
        key: 'total_incl_tax',
        label: 'Total (incl. tax)',
        getValue: (m) => Number(m.total_amount_incl_tax ?? 0),
        render: (m) => formatCurrency(m.total_amount_incl_tax ?? 0),
        numeric: true,
      },
      {
        key: 'tax',
        label: 'Tax amount',
        getValue: (m) =>
          m.total_tax_amount == null ? '' : Number(m.total_tax_amount),
        render: (m) =>
          m.total_tax_amount != null ? formatCurrency(m.total_tax_amount) : '-',
        numeric: true,
      },
      {
        key: 'line_count',
        label: 'Line count',
        getValue: (m) => m.line_count ?? '',
        render: (m) => (m.line_count != null ? String(m.line_count) : '-'),
        numeric: true,
      },
      {
        key: 'line_signature',
        label: 'Line signature',
        getValue: (m) => m.line_signature || '',
        render: (m) => truncatedHash(m.line_signature),
      },
      {
        key: 'return_for',
        label: 'Return of',
        getValue: (m) => m.return_for || '',
        render: (m) => truncatedHash(m.return_for),
      },
      {
        key: 'score',
        label: 'Match score',
        getValue: (m) => m.score ?? 0,
        render: (m) => (m.score != null ? `${m.score}%` : '-'),
        neverDiff: true,
        numeric: true,
      },
    ],
  }
